/**
 * UTILIDADES DE REDIRECCIÓN DE AUTENTICACIÓN
 *
 * Empresa y Houseme usan login embebido (NO Accounts).
 * Ver: docs/auth/EMPRESA_EMBEDDED_AUTH.md
 *
 * @package @shared/navigation
 */

import { getAppConfig } from './appConfig';
import { logger } from '../utils/logger';
import { supabase } from '../lib/supabaseClient';

/**
 * URL del servicio de autenticación centralizado
 * 
 * 🔐 REGLA PERMANENTE: ACCOUNTS_URL SIEMPRE viene de VITE_ACCOUNTS_URL
 * 
 * - DEV: Debe ser http://localhost:5176 (definido en .env.local)
 * - PROD: Debe ser el dominio real (definido en variables de entorno del hosting)
 * 
 * 🚫 PROHIBIDO:
 * - Hardcodear dominios
 * - Inferir dominio por código
 * - Fallback silencioso a producción
 */
const getAccountsUrl = (): string => {
  const accountsUrl = import.meta.env.VITE_ACCOUNTS_URL;
  
  if (!accountsUrl) {
    // En DEV, usar puerto directo de Accounts
    if (import.meta.env.DEV) {
      const devFallback = 'http://localhost:5176';
      console.warn(
        '⚠️ VITE_ACCOUNTS_URL no está definida. Usando fallback DEV:',
        devFallback,
        '\n💡 Agrega VITE_ACCOUNTS_URL=http://localhost:5176 a tu .env.local'
      );
      return devFallback;
    }
    
    // En PROD, NO hay fallback - debe estar definido
    const error = new Error(
      'VITE_ACCOUNTS_URL no está definida. Es obligatoria en producción.'
    );
    console.error('❌', error.message);
    throw error;
  }
  
  // ✅ Accounts se mantiene en su puerto directo (5176) - NO se fuerza proxy
  // NOTA: Esto significa que localStorage NO se comparte entre apps en DEV
  // Cada app tendrá que iniciar sesión por separado
  
  // Validación: En DEV, no permitir URLs de producción
  if (import.meta.env.DEV && accountsUrl.startsWith('https://')) {
    console.warn(
      '⚠️ ACCOUNTS_URL apunta a PROD en DEV:',
      accountsUrl,
      '\n💡 En desarrollo, debe ser http://localhost:5176'
    );
  }
  
  return accountsUrl;
};

export const ACCOUNTS_URL = getAccountsUrl();

/**
 * Redirige al login centralizado con el skin de la app
 * 
 * ⚠️ SIEMPRE usa redirección externa (window.location.href) - Accounts es una app separada
 * 
 * @param appName - Nombre de la app para aplicar skin (ej: 'empresa', 'tienda')
 * @param returnTo - URL a la que redirigir después del login (opcional)
 * @param _navigate - Función de navegación de React Router (ignorada - siempre se usa window.location.href)
 */
export function redirectToLogin(
  appName?: string,
  returnTo?: string,
  _navigate?: (path: string) => void
): void {
  // 🔍 DEBUG: Log de redirectToLogin
  console.log('%c[REDIRECT_TO_LOGIN_DEBUG] 🚨 redirectToLogin llamado', 'color: #ef4444; font-weight: bold; font-size: 16px;', {
    appName,
    returnTo,
    currentUrl: window.location.href,
    currentPathname: window.location.pathname,
    stackTrace: new Error().stack,
    timestamp: new Date().toISOString(),
  });
  
  // 🔍 CRÍTICO: Detectar si ya estamos en Accounts/login para evitar loops de encoding
  const currentUrl = window.location.href;
  const isAlreadyOnAccountsLogin = currentUrl.includes('/accounts/login') || currentUrl.includes('/login');
  
  let finalReturnTo: string;
  
  if (returnTo && returnTo.startsWith('http') && returnTo !== window.location.origin) {
    // Si se proporciona un returnTo válido, verificar que no apunte a Accounts/login
    const returnToUrl = new URL(returnTo);
    const returnToPointsToAccounts = returnToUrl.pathname.includes('/accounts/login') || returnToUrl.pathname.includes('/login');
    
    if (returnToPointsToAccounts) {
      // ⚠️ CRÍTICO: Si returnTo apunta a Accounts/login, extraer el returnTo anidado o usar dominio de app
      const returnToParams = new URLSearchParams(returnToUrl.search);
      const nestedReturnTo = returnToParams.get('returnTo');
      
      if (nestedReturnTo && !nestedReturnTo.includes('/accounts/login') && !nestedReturnTo.includes('/login')) {
        // Usar el returnTo anidado si no apunta a Accounts
        finalReturnTo = nestedReturnTo;
      } else {
        // Si el returnTo anidado también apunta a Accounts o no existe, usar dominio de app
        const appConfig = getAppConfig(appName || 'empresa');
        finalReturnTo = appConfig?.url || window.location.origin;
      }
    } else {
      // returnTo válido que no apunta a Accounts, usarlo
      finalReturnTo = returnTo;
    }
  } else if (isAlreadyOnAccountsLogin) {
    // ⚠️ CRÍTICO: Si ya estamos en Accounts/login, extraer el returnTo original de los query params
    // para evitar loops de encoding
    const currentParams = new URLSearchParams(window.location.search);
    const originalReturnTo = currentParams.get('returnTo');
    
    if (originalReturnTo) {
      // Verificar que el returnTo original no apunte a Accounts/login
      try {
        const originalUrl = new URL(originalReturnTo);
        const originalPointsToAccounts = originalUrl.pathname.includes('/accounts/login') || originalUrl.pathname.includes('/login');
        
        if (originalPointsToAccounts) {
          // Si el returnTo original apunta a Accounts, extraer el returnTo anidado
          const originalParams = new URLSearchParams(originalUrl.search);
          const nestedReturnTo = originalParams.get('returnTo');
          
          if (nestedReturnTo && !nestedReturnTo.includes('/accounts/login') && !nestedReturnTo.includes('/login')) {
            finalReturnTo = nestedReturnTo;
          } else {
            // Si el returnTo anidado también apunta a Accounts, usar dominio de app
            const appConfig = getAppConfig(appName || 'empresa');
            finalReturnTo = appConfig?.url || window.location.origin;
          }
        } else {
          // returnTo original válido que no apunta a Accounts
          finalReturnTo = originalReturnTo;
        }
      } catch {
        // Si no se puede parsear como URL, usar dominio de app
        const appConfig = getAppConfig(appName || 'empresa');
        finalReturnTo = appConfig?.url || window.location.origin;
      }
    } else {
      // Si no hay returnTo original, usar el dominio de la app (no la URL de login)
      const appConfig = getAppConfig(appName || 'empresa');
      finalReturnTo = appConfig?.url || window.location.origin;
    }
  } else {
    // ✅ Normalizar: Si no estamos en Accounts, convertir URL absoluta a ruta relativa
    const currentUrl = window.location.href;
    try {
      const url = new URL(currentUrl);
      // Si la URL es del mismo origen, usar solo pathname + search + hash
      if (url.origin === window.location.origin) {
        let relativePath = url.pathname + url.search + url.hash;
        // ✅ CRÍTICO: Si es /login, normalizar a / (Landing)
        if (relativePath === '/login' || relativePath.startsWith('/login?')) {
          relativePath = '/';
        }
        finalReturnTo = relativePath;
      } else {
        // Origen diferente (no debería pasar en DEV/PROD normal)
        finalReturnTo = currentUrl;
      }
    } catch {
      // Si no se puede parsear, usar pathname directamente
      let relativePath = window.location.pathname + window.location.search;
      if (relativePath === '/login' || relativePath.startsWith('/login?')) {
        relativePath = '/';
      }
      finalReturnTo = relativePath;
    }
  }

  // Construir URL completa de login en Accounts
  // ⚠️ IMPORTANTE: URLSearchParams.set() ya encodeará automáticamente los valores
  // NO usar encodeURIComponent() aquí para evitar doble encoding
  const params = new URLSearchParams();
  
  if (appName) {
    params.set('app', appName);
  }
  
  // ✅ SIEMPRE incluir returnTo completo (ruta + path)
  params.set('returnTo', finalReturnTo);

  // Accounts en producción + app en localhost: el handoff debe volver al origen local (no al dominio prod).
  try {
    const o = new URL(window.location.href);
    const h = o.hostname.toLowerCase();
    if (h === 'localhost' || h === '127.0.0.1') {
      params.set('clientOrigin', o.origin);
    }
  } catch {
    /* ignore */
  }
  
  // ✅ Empresa y Houseme usan login embebido — NUNCA redirigir a Accounts externo
  if (appName === 'empresa') {
    const returnPath = finalReturnTo.startsWith('http')
      ? new URL(finalReturnTo).pathname + new URL(finalReturnTo).search
      : finalReturnTo;
    const loginPath = `/login?returnTo=${encodeURIComponent(returnPath === '/login' ? '/' : returnPath)}`;
    if (_navigate) {
      _navigate(loginPath);
    } else {
      window.location.href = loginPath;
    }
    return;
  }

  if (appName === 'houseme') {
    const returnPath = finalReturnTo.startsWith('http')
      ? new URL(finalReturnTo).pathname + new URL(finalReturnTo).search
      : finalReturnTo;
    const welcomePath = `/welcome?returnTo=${encodeURIComponent(returnPath === '/welcome' ? '/mehouse' : returnPath)}`;
    if (_navigate) {
      _navigate(welcomePath);
    } else {
      window.location.href = welcomePath;
    }
    return;
  }

  // ✅ GobScore usa login embebido (modal, no ruta) — NUNCA redirigir a
  // Accounts externo. El bridge se registra en apps/gobscore/src/lib/
  // loginModalBridge.ts al montar LoginModalProvider; packages/shared no
  // puede importar de una app especifica, por eso se expone en `window`.
  if (appName === 'gobscore') {
    const returnPath = finalReturnTo.startsWith('http')
      ? new URL(finalReturnTo).pathname + new URL(finalReturnTo).search
      : finalReturnTo;
    const bridge = typeof window !== 'undefined' ? window.__gobscoreRequestLoginModal : undefined;
    if (bridge) {
      bridge(returnPath);
    }
    // Si el bridge todavia no esta montado (carga muy temprana), no hacemos
    // nada en vez de caer al redirect externo de Accounts -- mejor no pedir
    // login todavia que mandar a un usuario anonimo de gobscore a otra app.
    return;
  }

  // Construir URL completa: ACCOUNTS_URL/login?app=...&returnTo=...
  const loginUrl = `${ACCOUNTS_URL}/login${params.toString() ? `?${params.toString()}` : ''}`;

  logger.debug(`🔐 redirectToLogin - Redirigiendo externamente a: ${loginUrl}`, {
    currentUrl,
    isAlreadyOnAccountsLogin,
    finalReturnTo,
  });

  // ⚠️ SIEMPRE usar window.location.href - Accounts es una app externa
  // NO usar navigate() porque Accounts no está montado como módulo
  window.location.href = loginUrl;
}

/**
 * Redirige al registro centralizado con el skin de la app
 * 
 * ⚠️ SIEMPRE usa redirección externa (window.location.href) - Accounts es una app separada
 * 
 * @param appName - Nombre de la app para aplicar skin (ej: 'empresa', 'tienda')
 * @param returnTo - URL a la que redirigir después del registro (opcional)
 */
export function redirectToSignup(
  appName?: string,
  returnTo?: string
): void {
  const params = new URLSearchParams();
  
  if (appName) {
    params.set('app', appName);
  }
  
  if (returnTo) {
    params.set('returnTo', returnTo);
  }

  try {
    const o = new URL(window.location.href);
    const h = o.hostname.toLowerCase();
    if (h === 'localhost' || h === '127.0.0.1') {
      params.set('clientOrigin', o.origin);
    }
  } catch {
    /* ignore */
  }

  const signupUrl = `${ACCOUNTS_URL}/signup${params.toString() ? `?${params.toString()}` : ''}`;
  
  logger.debug(`🔐 redirectToSignup - Redirigiendo a: ${signupUrl}`);
  
  // ⚠️ SIEMPRE usar window.location.href - Accounts es una app externa
  window.location.href = signupUrl;
}

/**
 * Redirige al logout centralizado
 *
 * El logout mata la sesión para TODAS las apps.
 * Si se pasa appName, Accounts redirigirá al login con el skin de esa app (ej: empresa).
 *
 * @param appName - Opcional. App a la que volver tras el logout (ej: 'empresa', 'houseme')
 */
export function redirectToLogout(appName?: string): void {
  // ✅ Empresa usa login embebido — logout local, no redirigir a Accounts externo
  if (appName === 'empresa') {
    try {
      localStorage.removeItem('theplatform360:active_empresa_id');
      supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    } catch { /* ignore */ }
    window.location.href = '/login';
    return;
  }

  const params = new URLSearchParams();
  if (appName) params.set('app', appName);
  const query = params.toString();
  const logoutUrl = `${ACCOUNTS_URL}/logout${query ? `?${query}` : ''}`;
  logger.debug(`🔐 redirectToLogout - Redirigiendo a: ${logoutUrl}`);
  window.location.href = logoutUrl;
}

/**
 * Redirige a la app correspondiente después del login
 * 
 * @param appName - Nombre de la app a la que redirigir
 * @param returnTo - URL específica a la que redirigir (opcional, sobrescribe la URL por defecto de la app)
 * @param intent - Intent de autenticación (ej: "admin_access")
 * @param source - Origen de la solicitud (ej: "thefomlabs")
 * @param navigate - Función de navegación de React Router (opcional, para DEV sin reload)
 */
export async function redirectAfterLogin(
  appName?: string, 
  returnTo?: string, 
  intent?: string | null, 
  source?: string | null,
  navigate?: (path: string) => void
): Promise<void> {
  
  console.log('[REDIRECT] Ejecutando redirectAfterLogin con app:', appName, 'returnTo:', returnTo, 'intent:', intent, 'source:', source);
  
  
  
  const isDev = import.meta.env.DEV;
  
  // Si hay intent de admin_access, redirigir a TheFomLabs con los query params
  if (intent === 'admin_access') {
    const thefomlabsUrl = getAppConfig('thefomlabs')?.url || 'http://localhost:5173';
    const redirectUrl = new URL(thefomlabsUrl);
    redirectUrl.searchParams.set('intent', intent);
    if (source) {
      redirectUrl.searchParams.set('source', source);
    }
    console.log('[REDIRECT] Redirigiendo a TheFomLabs con intent admin_access:', redirectUrl.toString());
    
    logger.debug(`🔐 redirectAfterLogin - Redirigiendo a TheFomLabs con intent admin_access: ${redirectUrl.toString()}`);
    // En DEV, redirecciones externas siempre requieren window.location.href
    window.location.href = redirectUrl.toString();
    return;
  }
  
  if (returnTo) {
    // Si hay returnTo, usarlo directamente
    console.log('[REDIRECT] Usando returnTo directamente:', returnTo);
    
    logger.debug(`🔐 redirectAfterLogin - Redirigiendo a returnTo: ${returnTo}`);
    
    // En DEV, si returnTo es una ruta relativa y tenemos navigate, intentar SPA navigation
    // Pero si es una URL absoluta o de otra app, usar window.location.href
    if (isDev && navigate && returnTo.startsWith('/')) {
      // Es una ruta relativa - podemos usar navigate (si está dentro de la misma app)
      // Pero esto solo funciona si returnTo es una ruta dentro de Accounts
      // Si es /empresa, /houseme, etc., no funciona porque son apps diferentes
      // Por ahora, siempre usar window.location.href para returnTo
      window.location.href = returnTo;
    } else {
      window.location.href = returnTo;
    }
    return;
  }

  // Normalizar appName: asegurar que sea string válido
  const normalizedAppName = appName && appName.trim() ? appName.trim() : undefined;
  
  if (!normalizedAppName) {
    // Si no hay app, redirigir a accounts (o página por defecto)
    
    logger.debug(`🔐 redirectAfterLogin - Sin app especificada, redirigiendo a accounts`);
    window.location.href = ACCOUNTS_URL;
    return;
  }

  const appConfig = getAppConfig(normalizedAppName);
  if (!appConfig) {
    
    logger.warn(`🔐 redirectAfterLogin - App no encontrada: ${normalizedAppName}, redirigiendo a accounts`);
    window.location.href = ACCOUNTS_URL;
    return;
  }

  // Verificar sesión antes de redirigir
  try {
    const { data: { session: sessionBeforeRedirect } } = await supabase.auth.getSession();
    
    logger.debug(`🔐 redirectAfterLogin - Redirigiendo a app ${normalizedAppName}: ${appConfig.url}`);
  } catch (error) {
    
  }
  
  // NOTA: En DEV con proxy, aunque las apps están bajo el mismo origen (localhost:5173),
  // cada app tiene su propio router React. Por lo tanto, redirecciones entre apps
  // (Accounts → Empresa, Accounts → Houseme, etc.) siempre requieren window.location.href.
  // 
  // Solo podemos usar navigate() para rutas dentro de la misma app.
  // Como después del login siempre redirigimos a otra app, siempre usamos window.location.href.
  // 
  // El event.preventDefault() en el formulario previene recargas innecesarias durante
  // la validación del formulario, pero la redirección final a otra app requiere recarga.
  window.location.href = appConfig.url;
}

/**
 * Obtiene el parámetro 'app' de la URL actual
 * 
 * @returns Nombre de la app o null
 */
export function getAppFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('app');
}

/**
 * Obtiene el parámetro 'returnTo' de la URL actual
 * 
 * @returns URL de retorno o null
 */
export function getReturnToFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('returnTo');
}

